import type { PlanSlug } from '@/api/types';
import { formatPrice, type PlanPrices } from '@/billing/useSubscriptionPlans';
import { useT } from '@/i18n/LocaleProvider';
import { cx } from '@/lib/cx';
import styles from './Paywall.module.css';

interface Props {
  prices: PlanPrices;
  value: PlanSlug;
  onChange: (plan: PlanSlug) => void;
  /** Текущий тариф активной подписки — карточка помечена и не выбирается. */
  current?: PlanSlug | null;
  /** 32px в paywall, 30px на странице биллинга (макет). */
  size?: 'lg' | 'md';
  disabled?: boolean;
}

/** Две карточки тарифов (BILL-6): цены только из /subscription-plans, скидка считается. */
export function PlanCards({
  prices,
  value,
  onChange,
  current = null,
  size = 'lg',
  disabled,
}: Props) {
  const t = useT();
  const cards: Array<{ slug: PlanSlug; name: string; period: string }> = [
    { slug: 'monthly', name: t('billing.monthly'), period: t('billing.perMonth') },
    { slug: 'yearly', name: t('billing.yearly'), period: t('billing.perYear') },
  ];
  return (
    <div
      className={cx(styles.plans, size === 'md' && styles.plansMd)}
      role="radiogroup"
      aria-label={t('billing.plan')}
    >
      {cards.map((c) => {
        const isCurrent = current === c.slug;
        const plan = c.slug === 'yearly' ? prices.yearly : prices.monthly;
        return (
          <button
            key={c.slug}
            type="button"
            role="radio"
            aria-checked={value === c.slug}
            aria-label={t('billing.planAria', {
              name: c.name,
              price: formatPrice(plan),
              period: c.period,
            })}
            className={cx(
              styles.plan,
              value === c.slug && styles.planSelected,
              isCurrent && styles.planCurrent,
            )}
            onClick={() => !isCurrent && onChange(c.slug)}
            disabled={disabled || isCurrent}
          >
            {c.slug === 'yearly' && prices.yearlyDiscountPct ? (
              <span className={styles.planBadge}>−{prices.yearlyDiscountPct}%</span>
            ) : null}
            {isCurrent ? <span className={styles.planBadge}>{t('billing.current')}</span> : null}
            <span className={styles.planName}>{c.name}</span>
            <span className={styles.planPrice}>
              {plan ? (
                formatPrice(plan)
              ) : (
                <span className={styles.pricePlaceholder} aria-label={t('billing.loadingPrice')} />
              )}
              <span className={styles.planPeriod}> {c.period}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
